import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Download, Mail, CheckCircle2, AlertCircle } from "lucide-react";
import { API, Api } from "../lib/api";
import BookingsTable from "./admin/BookingsTable";

function TokenInput({ token, onChange }) {
  return (
    <label className="block">
      <span className="text-sm font-bold text-navy">Admin token</span>
      <input
        value={token}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Paste your token"
        className="mt-2 w-full rounded-xl border border-[#EFE7D2] p-3 bg-cream/40 font-mono text-sm"
        data-testid="admin-token-input"
        autoComplete="off"
      />
    </label>
  );
}

function ActionButtons({ downloadUrl, onEmail, token, sending }) {
  return (
    <div className="flex flex-wrap gap-3 pt-2">
      <a
        href={downloadUrl || "#"}
        aria-disabled={!downloadUrl}
        className={`btn-primary ${!downloadUrl ? "opacity-50 pointer-events-none" : ""}`}
        data-testid="download-export-btn"
      >
        <Download size={16} /> Download Excel
      </a>
      <button
        onClick={onEmail}
        disabled={!token || sending}
        className="inline-flex items-center gap-2 rounded-round bg-navy text-cream font-bold px-5 py-3 disabled:opacity-50"
        data-testid="email-export-btn"
      >
        <Mail size={16} /> {sending ? "Sending…" : "Email me the export"}
      </button>
    </div>
  );
}

function ResultPanel({ result }) {
  if (!result) return null;
  return (
    <div
      className="flex items-start gap-2 p-3 rounded-xl bg-coral/5 border border-coral/30 text-sm"
      data-testid="email-result"
    >
      <CheckCircle2 size={16} className="text-coral mt-0.5" />
      <div>
        <p className="font-bold">
          Email {result.sent ? "sent" : "queued (no provider)"}
        </p>
        <p className="text-grey">
          {result.bookings} booking{result.bookings === 1 ? "" : "s"} ·{" "}
          {result.invites} invite{result.invites === 1 ? "" : "s"} ·{" "}
          {result.filename}
        </p>
      </div>
    </div>
  );
}

function ErrorPanel({ error }) {
  if (!error) return null;
  return (
    <div
      className="flex items-start gap-2 p-3 rounded-xl bg-red-50 border border-red-200 text-sm"
      data-testid="admin-error"
    >
      <AlertCircle size={16} className="text-red-600 mt-0.5" />
      <p className="text-red-700">{error}</p>
    </div>
  );
}

function useAdminExport(initialToken) {
  const [token, setToken] = useState(initialToken);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const downloadUrl = useMemo(
    () =>
      token
        ? `${API}/admin/export.xlsx?token=${encodeURIComponent(token)}`
        : null,
    [token]
  );

  async function sendDigestEmail() {
    if (!token) return;
    setSending(true);
    setError(null);
    setResult(null);
    try {
      setResult(await Api.sendAdminExport(token));
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Could not send");
    } finally {
      setSending(false);
    }
  }

  return { token, setToken, sending, result, error, downloadUrl, sendDigestEmail };
}

export default function AdminPage() {
  const [sp] = useSearchParams();
  const f = useAdminExport(sp.get("token") || "");

  return (
    <div
      className="container-x py-12 md:py-20 max-w-6xl"
      data-testid="admin-page"
    >
      <p className="eyebrow">Admin</p>
      <h1 className="heading-xl mt-2">Bookings export</h1>
      <p className="body-lead mt-3">
        Download all bookings + invites as an Excel file, or email the
        latest copy to the digest address on file.
      </p>

      <section className="mt-8 card p-6 space-y-4">
        <TokenInput token={f.token} onChange={f.setToken} />
        <ActionButtons
          downloadUrl={f.downloadUrl}
          onEmail={f.sendDigestEmail}
          token={f.token}
          sending={f.sending}
        />
        <ResultPanel result={f.result} />
        <ErrorPanel error={f.error} />
      </section>

      <section className="mt-6 card p-6">
        <BookingsTable token={f.token} />
      </section>

      <p className="text-xs text-grey mt-6 leading-relaxed">
        A daily digest is also emailed automatically every morning to the
        address configured server-side. Rows are tagged{" "}
        <b>NEW</b>, <b>CONFIRMED</b>, or <b>CANCELLED</b>.
      </p>
    </div>
  );
}
